import type { FC } from 'react';
import * as React from 'react';
import { Card, Stack, styled, useTheme } from '@mui/material';
import IconWallet from '~icons/ant-design/wallet-filled';
import IconFileFill from '~icons/eva/file-fill';
import { TotalAnalytic } from './TotalAnalytic';
import { useTranslation } from 'react-i18next';
import type { CashList } from '@pflegenavi/frontend/api-nursing-home';
import { useCashListIcon } from '@pflegenavi/web-components';
import { CashListStorageType } from '@pflegenavi/shared/api';

interface NewAccountingOverviewTotalsMangopayProps {
  walletInCent: number;
  receiptsInCent: number;
  cashLists: CashList[];
  receiptManagementEnabled: boolean;
}

export const NewAccountingOverviewTotalsMangopay: FC<
  NewAccountingOverviewTotalsMangopayProps
> = (props) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const { cashLists, walletInCent, receiptsInCent, receiptManagementEnabled } =
    props;

  const bankAccountCashLists = cashLists.filter(
    (cashList) =>
      cashList && cashList.storageType === CashListStorageType.BankAccount
  );

  const bankAccountAmountInCent = bankAccountCashLists.reduce(
    (acc, cashList) => acc + cashList.totalInCent,
    0
  );

  const cashAccountCashLists = cashLists.filter(
    (cashList) => cashList.storageType & CashListStorageType.Cash
  );

  const cashAccountAmountInCent = cashAccountCashLists.reduce(
    (acc, cashList) => acc + cashList.totalInCent,
    0
  );

  const totalInCent =
    (receiptManagementEnabled ? walletInCent + receiptsInCent : 0) +
    bankAccountAmountInCent +
    cashAccountAmountInCent;

  const percentages: {
    [key: string]: number;
  } = {
    wallet: (walletInCent / totalInCent) * 100,
    receipts: (receiptsInCent / totalInCent) * 100,
  };

  for (const bankCashList of bankAccountCashLists) {
    percentages[bankCashList.name] =
      (bankCashList.totalInCent / totalInCent) * 100;
  }

  for (const cashCashList of cashAccountCashLists) {
    percentages[cashCashList.name] =
      (cashCashList.totalInCent / totalInCent) * 100;
  }

  const totalItems =
    bankAccountCashLists.length +
    cashAccountCashLists.length +
    (receiptManagementEnabled ? 2 : 0);

  return (
    <Stack
      direction="row"
      sx={{
        width: '100%',
        flexWrap: 'wrap',
        gap: totalItems > 4 ? 3 : 1.5,
        rowGap: totalItems > 4 ? 3 : 1.5,
      }}
    >
      {receiptManagementEnabled && (
        <CardContainer totalItems={totalItems}>
          <TotalAnalytic
            sx={{
              width: 'fit-content',
              minWidth: 190,
              padding: { xs: 0 },
              justifyContent: 'flex-start',
            }}
            title="Wallet"
            percent={percentages.wallet}
            amountInCent={walletInCent}
            Icon={IconWallet}
            color={theme.palette.info.main}
          />
        </CardContainer>
      )}

      {bankAccountCashLists.map((cashList) => (
        <CashListCard
          key={cashList.id}
          cashList={cashList}
          totalItems={totalItems}
          percent={percentages[cashList.name]}
          color={theme.palette.warning.main}
        />
      ))}
      {cashAccountCashLists.map((cashList) => (
        <CashListCard
          key={cashList.id}
          cashList={cashList}
          totalItems={totalItems}
          percent={percentages[cashList.name]}
          color={theme.palette.success.main}
        />
      ))}

      {receiptManagementEnabled && (
        <CardContainer totalItems={totalItems}>
          <TotalAnalytic
            sx={{
              width: 'fit-content',
              minWidth: 190,
              padding: { xs: 0 },
              justifyContent: 'flex-start',
            }}
            title={t('receipts.main')}
            percent={percentages.receipts}
            amountInCent={receiptsInCent}
            Icon={IconFileFill}
            color={theme.palette.text.secondary}
          />
        </CardContainer>
      )}
    </Stack>
  );
};

const CardContainer = styled(Card)<{ totalItems: number }>(
  ({ totalItems }) => ({
    padding: totalItems > 4 ? 24 : 16,
    display: 'flex',
  })
);

interface CashListCardProps {
  cashList: CashList;
  totalItems: number;
  percent: number;
  color: string;
}

const CashListCard: FC<CashListCardProps> = ({
  cashList,
  totalItems,
  percent,
  color,
}) => {
  const Icon = useCashListIcon(cashList);
  return (
    <CardContainer totalItems={totalItems}>
      <TotalAnalytic
        sx={{
          width: 'fit-content',
          minWidth: 190,
          padding: { xs: 0 },
          justifyContent: 'flex-start',
        }}
        title={cashList.name}
        percent={percent}
        amountInCent={cashList.totalInCent}
        Icon={Icon}
        color={color}
      />
    </CardContainer>
  );
};
